import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  CircularProgress,
  Stack,
} from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon } from '@mui/icons-material';
import StyledButton from '../../common/StyledButton';
import { formatCheckInDateLabel } from '../../../utils/festivalDates';
import { useAuth } from '../../../context/AuthContext';

const ConfirmationStep = ({ vendorData: propVendorData, boothData: propBoothData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vendorData, setVendorData] = useState(propVendorData);
  const [boothData, setBoothData] = useState(propBoothData);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // If we already have the data from props, no need to fetch
        if (vendorData && boothData) {
          setLoading(false);
          return;
        }

        // Get payment_intent from URL
        const params = new URLSearchParams(location.search);
        const paymentIntentId = params.get('payment_intent');
        
        if (!paymentIntentId) {
          throw new Error('No payment information found');
        }

        // Fetch payment details
        const response = await fetch(`/api/payment/status/${paymentIntentId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch payment details');
        }

        const data = await response.json();
        
        // Set the data
        setVendorData({
          business_name: data.business_name,
          check_in_date: data.check_in_date,
          event_date: data.festival_event_date,
        });
        setBoothData({
          number: data.booth_number
        });
      } catch (err) {
        console.error('Error fetching payment details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [location.search, vendorData, boothData]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mt: 2, borderRadius: 2 }}>
          <Typography color="error" align="center">
            {error}
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {isAuthenticated && (
                <StyledButton
                  variant="outlined"
                  onClick={() => navigate('/vendor/dashboard')}
                  sx={{ minWidth: '200px' }}
                >
                  Vendor Dashboard
                </StyledButton>
              )}
              <StyledButton
                variant="contained"
                onClick={() => navigate('/')}
                sx={{ minWidth: '200px' }}
              >
                Return to Home
              </StyledButton>
            </Stack>
          </Box>
        </Paper>
      </Container>
    );
  }

  const hasDetails = vendorData && boothData;

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mt: 2, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
            Payment Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Thank you for your payment. Your registration is complete.
          </Typography>
        </Box>
        {hasDetails && (
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Registration Details
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Business Name
                      </Typography>
                      <Typography variant="body1">
                        {vendorData.business_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Booth Number
                      </Typography>
                      <Typography variant="body1">
                        {boothData.number}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Check-in Date
                      </Typography>
                      <Typography variant="body1">
                        {formatCheckInDateLabel(vendorData.event_date, vendorData.check_in_date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Status
                      </Typography>
                      <Typography variant="body1" color="success.main">
                        Paid
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Next Steps
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" paragraph>
                    1. Check your email for a detailed confirmation and receipt
                  </Typography>
                  <Typography variant="body2" paragraph>
                    2. Prepare your booth setup according to the guidelines
                  </Typography>
                  <Typography variant="body2" paragraph>
                    3. Arrive at your scheduled check-in time
                  </Typography>
                  <Typography variant="body2">
                    4. Bring any required documentation
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {isAuthenticated && (
              <StyledButton
                variant="outlined"
                onClick={() => navigate('/vendor/dashboard')}
                sx={{ minWidth: '200px' }}
              >
                Vendor Dashboard
              </StyledButton>
            )}
            <StyledButton
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ minWidth: '200px' }}
            >
              Return to Home
            </StyledButton>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmationStep; 